import { useAuth } from "@/context/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getDashboardSubscription,
  isDashboardBatchFetching,
  shouldDeferToDashboardBatch,
} from "@/utils/dashboardQueryCache";
import { buildSubscriptionStatusSnapshot } from "@/utils/subscriptionPlanState";
import {
  fetchSubscriptionStatusSnapshot,
  resolveSubscriptionDisplayName,
} from "@/hooks/useSubscriptionStatusHelpers";

export interface UseSubscriptionStatusOptions {
  /** When false, skips fetching (e.g. profile modal closed). */
  enabled?: boolean;
}

/**
 * Fetches and returns the subscription status of the authenticated user.
 */
export const useSubscriptionStatus = (options?: UseSubscriptionStatusOptions) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const cachedSubscription = getDashboardSubscription(queryClient, user?.id);
  const dashboardBatchFetching = isDashboardBatchFetching(queryClient, user?.id);
  const deferToDashboard = shouldDeferToDashboardBatch(queryClient, user?.id);

  const { data, isLoading } = useQuery({
    queryKey: ["subscription", user?.id],
    ...(cachedSubscription ? { initialData: cachedSubscription } : {}),
    queryFn: () => {
      if (!user) {
        return buildSubscriptionStatusSnapshot(null);
      }
      return fetchSubscriptionStatusSnapshot(user.id);
    },
    enabled:
      !!user &&
      (options?.enabled ?? true) &&
      !deferToDashboard &&
      (!!cachedSubscription || !dashboardBatchFetching),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: true,
    refetchInterval: 5 * 60 * 1000,
  });

  const resolved = cachedSubscription ?? data;
  const planDisplayName = resolveSubscriptionDisplayName(resolved?.planDisplayName);
  // While deferred or still fetching with no snapshot, treat as loading so Pro badges
  // do not flash incorrectly for paid users (shouldShowProBadge hides during load).
  const awaitingSnapshot = !resolved && (deferToDashboard || isLoading || dashboardBatchFetching);

  return {
    isPaid: resolved?.isPaid || false,
    tier: resolved?.tier || "free",
    planDisplayName,
    subscriptionTier: planDisplayName,
    endDate: resolved?.endDate,
    isLoading: awaitingSnapshot || (!deferToDashboard && isLoading),
  };
};
